import {
    translationRequestKey,
    type ReaderTranslation,
    type ReaderTranslationRequest,
} from './translationRequests';

type FetchTranslations = (
    requests: ReaderTranslationRequest[],
    signal: AbortSignal,
) => Promise<ReaderTranslation[]>;
export interface TranslationState {
    translation: ReaderTranslation | null;
    busy: boolean;
    unavailable: boolean;
}
interface Entry {
    request: ReaderTranslationRequest;
    original: string;
    state: TranslationState;
    enabled: boolean;
    checked: boolean;
    requested: boolean;
    polls: number;
    listeners: Set<(state: TranslationState) => void>;
}

// Keep recently displayed translations when a section closes, without retaining
// every piece of text visited through pagination during a long page visit.
const MAX_UNUSED_TRANSLATIONS = 200;

/** One owner for a page's read batches, explicit generation, and pending-result timer. */
export const createPageTranslations = (read: FetchTranslations, generate: FetchTranslations) => {
    const entries = new Map<string, Entry>();
    const requests = new Map<AbortController, Entry[]>();
    let active = true;
    let scheduled = false;
    let running = false;
    let rerun = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const publish = (entry: Entry, state: TranslationState) => {
        entry.state = state;
        entry.listeners.forEach((listener) => listener(state));
    };
    const current = (entry: Entry) =>
        active &&
        entry.listeners.size > 0 &&
        entries.get(translationRequestKey(entry.request, entry.original)) === entry;
    const retainCompleted = (key: string, entry: Entry) => {
        if (entry.state.translation?.status !== 'COMPLETED') {
            entries.delete(key);
            return;
        }
        // Map order records the most recently released translation last.
        entries.delete(key);
        entries.set(key, entry);
        const unused = [...entries].filter(([, value]) => !value.listeners.size);
        for (const [expired] of unused.slice(0, -MAX_UNUSED_TRANSLATIONS)) {
            entries.delete(expired);
        }
    };

    const fetchBatches = async (batch: Entry[], generating: boolean) => {
        for (let start = 0; active && start < batch.length; start += 25) {
            const chunk = batch.slice(start, start + 25).filter(current);
            if (!chunk.length) continue;
            for (const entry of chunk) {
                if (generating) entry.requested = true;
                publish(entry, { ...entry.state, busy: true, unavailable: false });
            }
            const controller = new AbortController();
            requests.set(controller, chunk);
            let results: ReaderTranslation[] = [];
            try {
                results = await (generating ? generate : read)(
                    chunk.map((entry) => ({
                        ...entry.request,
                        ...(generating && entry.state.translation?.status === 'FAILED'
                            ? { retryFailed: true }
                            : {}),
                    })),
                    controller.signal,
                );
            } catch {
                // Keep the original text and expose an explicit retry.
            } finally {
                requests.delete(controller);
            }
            for (const entry of chunk) {
                if (!current(entry)) continue;
                entry.checked = true;
                const result = results.find(
                    (value) =>
                        translationRequestKey(value, value.originalText) ===
                        translationRequestKey(entry.request, entry.original),
                );
                publish(entry, {
                    translation: result ?? null,
                    busy: Boolean(
                        entry.enabled && result?.status === 'PENDING' && entry.polls < 20,
                    ),
                    unavailable: !result || result.status === 'FAILED',
                });
            }
        }
    };

    const run = async () => {
        scheduled = false;
        if (!active) return;
        if (running) {
            rerun = true;
            return;
        }
        running = true;
        clearTimeout(timer);
        try {
            await fetchBatches(
                [...entries.values()].filter((entry) => entry.enabled && !entry.checked),
                false,
            );
            if (active) {
                await fetchBatches(
                    [...entries.values()].filter(
                        (entry) =>
                            entry.enabled &&
                            !entry.requested &&
                            ['MISSING', 'FAILED'].includes(entry.state.translation?.status ?? ''),
                    ),
                    true,
                );
            }
        } finally {
            running = false;
        }
        if (!active) return;
        const pending = [...entries.values()].filter(
            (entry) =>
                entry.enabled && entry.state.translation?.status === 'PENDING' && entry.polls < 20,
        );
        if (pending.length) {
            timer = setTimeout(() => {
                for (const entry of pending.filter(current)) {
                    entry.polls += 1;
                    entry.checked = false;
                }
                schedule();
            }, 1500);
        }
        if (rerun) {
            rerun = false;
            schedule();
        }
    };
    const schedule = () => {
        if (!active || scheduled) return;
        scheduled = true;
        queueMicrotask(() => void run());
    };

    return {
        register(
            request: ReaderTranslationRequest,
            original: string,
            listener: (state: TranslationState) => void,
        ) {
            const key = translationRequestKey(request, original);
            let entry = entries.get(key);
            if (!entry) {
                entry = {
                    request,
                    original,
                    state: { translation: null, busy: false, unavailable: false },
                    enabled: false,
                    checked: false,
                    requested: false,
                    polls: 0,
                    listeners: new Set(),
                };
                entries.set(key, entry);
            }
            entry.listeners.add(listener);
            listener(
                entry.enabled
                    ? entry.state
                    : { translation: null, busy: false, unavailable: false },
            );
            return () => {
                entry.listeners.delete(listener);
                if (!entry.listeners.size && entries.get(key) === entry) {
                    entry.enabled = false;
                    retainCompleted(key, entry);
                    for (const [controller, batch] of requests) {
                        if (!batch.some(current)) controller.abort();
                    }
                    if (
                        ![...entries.values()].some(
                            (value) =>
                                value.enabled && value.state.translation?.status === 'PENDING',
                        )
                    )
                        clearTimeout(timer);
                }
            };
        },
        enable(request: ReaderTranslationRequest, original: string) {
            const entry = entries.get(translationRequestKey(request, original));
            if (!entry || !active || entry.state.busy) return;
            entry.enabled = true;
            if (entry.state.unavailable || entry.polls >= 20) {
                entry.checked = false;
                entry.requested = false;
                entry.polls = 0;
            }
            publish(entry, {
                ...entry.state,
                busy: !entry.checked || entry.state.translation?.status === 'PENDING',
            });
            schedule();
        },
        activate() {
            active = true;
            schedule();
        },
        dispose() {
            active = false;
            clearTimeout(timer);
            entries.clear();
            for (const controller of requests.keys()) controller.abort();
            requests.clear();
        },
    };
};
